import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { closeSideBar } from 'actions';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Collapse, Menu, MenuItem, useTheme } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemLink from './ListItemLink';
import ListItemText from '@mui/material/ListItemText';
import moment from 'moment-timezone';
import { isRouteActive } from 'routing/utils';
import { useTranslation } from 'services/localization/useTranslation';

import { IconType } from 'react-icons';
import {
	RiHome5Line,
	RiAddBoxLine,
	RiArrowLeftRightLine,
	RiTableLine,
	RiPriceTag3Line,
	RiListSettingsLine,
	RiDownload2Line,
	RiDashboard3Line,
	RiTruckLine,
	RiApps2Line,
	RiMessage2Line,
	RiAddCircleLine,
	RiGroupLine,
	RiSettings4Line,
	RiArrowDownSLine,
	RiArrowUpSLine,
	RiQuestionLine,
} from 'react-icons/ri';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as UserActions from 'actions/UserActions';
import * as ViewActions from 'actions/ViewActions';

import { Routes, push } from 'routing';
import classNames from 'classnames';
import ActiveLocationSelect from 'components/ActiveLocationSelect';
import { useHasFeature } from 'hooks/useHasFeature';
import EarnCreditsBanner from './EarnCreditsBanner';
import { hideEarnCreditsBanner } from 'services/earnCredits/utils';
import { logEvent } from 'common/services/analytics';
import { isSubscriptionPlan } from 'common/modules/plans';
import { hasHigherOrEqualAccess } from 'common/modules/users/utils';
import * as sleekPlan from 'common/services/analytics/sleekplan';

interface NavItem {
	label: string;
	Icon: IconType;
	ActiveIcon?: IconType;
	to: string;
	hidden?: boolean;
	locked?: boolean;
	disabled?: boolean;
}

const SideBar = () => {
	const dispatch = useDispatch();
	const classes = useStyles();
	const location = useLocation();
	const { t } = useTranslation();
	const theme = useTheme();
	const { hasFeature } = useHasFeature();

	const [bottomMenuAnchor, setBottomMenuAnchor] = useState<HTMLElement | null>(null);
	const [showHelp, setShowHelp] = useState<boolean>(false);

	const user = useSelector(UserSelectors.userData);
	const userId = useSelector(UserSelectors.activeUserId);
	const userRoles = useSelector(UserSelectors.userRoles);
	const userMerchantPreferences = useSelector(UserSelectors.userMerchantLevelPreferences);
	const sideBarOpen = useSelector(ViewSelectors.sideBarOpen);
	const shopInfo = useSelector(ShopSelectors.activeShopInfo);
	const shopPlan = useSelector(ShopSelectors.shopPlan)!;
	const isCreditsBannerHidden = userMerchantPreferences?.hideEarnCreditsBanner;
	const has7DaysPassedSinceSignUp = moment().diff(moment(shopInfo?.createdAt), 'days') > 7;
	const isManagerOrAbove = hasHigherOrEqualAccess(userRoles, ['manager']);

	const showEarnCreditsBanner =
		!isCreditsBannerHidden &&
		has7DaysPassedSinceSignUp &&
		isSubscriptionPlan(shopPlan) &&
		isManagerOrAbove;

	const shopLocationsCount = useSelector(ShopSelectors.allShopLocationsCount);

	const isAccountRouteActive = isRouteActive(Routes.ACCOUNT, location.pathname);

	useEffect(() => {
		sleekPlan.setUser(user);
	}, [user]);

	useEffect(() => {
		!sideBarOpen && sleekPlan.close();
	}, [sideBarOpen]);

	const NavItems: NavItem[] = [
		{
			label: t('headerBar.home', 'Home'),
			Icon: RiHome5Line,
			to: Routes.HOME_VIEW,
		},
		{
			label: t('headerBar.createOrder', 'Create order'),
			Icon: RiAddBoxLine,
			to: Routes.NEW_RENTAL,
			locked: !hasFeature('NEW_RENTAL', { scope: 'shop' }),
			hidden: !hasFeature('NEW_RENTAL', { scope: 'user' }),
		},
		{
			label: t('headerBar.orders', 'Orders'),
			Icon: RiArrowLeftRightLine,
			to: Routes.BOOKINGS,
		},
		{
			label: 'SKIDATA',
			Icon: RiDownload2Line,
			to: Routes.SKIDATA,
			hidden: !hasFeature('SKIDATA', { scope: 'shop' }),
		},
		{
			label: t('headerBar.catalog', 'Catalog'),
			Icon: RiPriceTag3Line,
			to: Routes.CATALOG,
			hidden: !hasFeature('PRODUCTS'),
		},
		{
			label: t('headerBar.inventory', 'Inventory'),
			Icon: RiTableLine,
			to: Routes.INVENTORY,
			hidden: !hasFeature('INVENTORY'),
		},
		{
			label: t('headerBar.customers', 'Customers'),
			Icon: RiGroupLine,
			to: Routes.CUSTOMERS,
		},
		{
			label: t('delivery.delivery', 'Delivery'),
			Icon: RiTruckLine,
			to: Routes.DELIVERY,
			locked: !hasFeature('DELIVERY_SERVICE', { scope: 'shop' }),
			hidden: !hasFeature('DELIVERY_SERVICE', { scope: 'user' }),
		},
		{
			label: t('headerBar.dashboard'),
			Icon: RiDashboard3Line,
			to: Routes.DASHBOARD,
			hidden: !hasFeature('DASHBOARD'),
		},
		{
			label: t('headerBar.settings'),
			Icon: RiListSettingsLine,
			to: Routes.STORE,
			hidden: !hasFeature('STORE_SETTINGS'),
		},
		{
			label: t('addOns.pageTitle'),
			Icon: RiApps2Line,
			to: Routes.BLOCKS_STORE,
		},
	];

	const openBottomMenu = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setBottomMenuAnchor(event.currentTarget);
	};
	const closeBottomMenu = () => {
		setBottomMenuAnchor(null);
	};

	const signOut = () => {
		sleekPlan.close();
		sleekPlan.resetUser();
		dispatch(UserActions.logout());
		dispatch(closeSideBar());
		push(Routes.LOGIN);
	};

	return (
		<Drawer
			open={sideBarOpen}
			onClose={() => {
				return dispatch(closeSideBar());
			}}
		>
			<div className={classes.sidebarContainer}>
				{shopLocationsCount > 1 && (
					<Box mt={1} mb={0}>
						<ActiveLocationSelect />
					</Box>
				)}
				<List component="nav" className={classes.sidebarMain}>
					{NavItems.map(({ label, to, Icon, ActiveIcon, hidden, locked, disabled }) => {
						if (hidden) return null;
						const isActive = isRouteActive(to, location.pathname);
						return (
							<ListItemLink
								key={`${label}${to}`}
								button
								disabled={disabled}
								to={to}
								className={classNames(classes.navItem, {
									[classes.navItemActive]: isActive,
								})}
								onClick={() => dispatch(closeSideBar())}
							>
								<ListItemIcon
									className={classNames(classes.navItemIcon, {
										[classes.navItemIconActive]: isActive && !disabled && !locked,
									})}
								>
									{locked && (
										<Box className={classes.navItemIconPlus}>
											<RiAddCircleLine color="inherit" size={16} />
										</Box>
									)}
									{isActive && ActiveIcon ? (
										<ActiveIcon opacity={locked ? 0.3 : 1} />
									) : (
										<Icon opacity={locked ? 0.3 : 1} />
									)}
								</ListItemIcon>
								<ListItemText
									primary={label}
									primaryTypographyProps={{
										sx: {
											fontSize: 14,
										},
									}}
									className={classNames({
										[classes.navItemTextLocked]: locked,
									})}
								/>
							</ListItemLink>
						);
					})}
				</List>
				<List className={classes.sidebarBottom}>
					<div className={classes.divider} />
					<ListItem
						className={classNames(classes.listItem, {
							[classes.navItemActive]: isAccountRouteActive,
						})}
						button
						onClick={() => {
							push(Routes.ACCOUNT);
							dispatch(closeSideBar());
						}}
						sx={{ mt: 1 }}
					>
						<ListItemIcon
							className={classNames(classes.listItemIcon, {
								[classes.navItemIconActive]: isAccountRouteActive,
							})}
						>
							<RiSettings4Line size={20} />
						</ListItemIcon>
						<ListItemText
							primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: '400 !important' } }}
							primary={t('account.pageTitle')}
						/>
					</ListItem>
					<ListItem className={classes.listItem} button onClick={() => setShowHelp(!showHelp)}>
						<ListItemIcon className={classes.listItemIcon}>
							<RiQuestionLine size={20} />
						</ListItemIcon>
						<ListItemText
							primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: '400 !important' } }}
							primary={t('common:help', 'Help')}
						/>
						<ListItemIcon className={classes.listItemIcon} sx={{ minWidth: 0 }}>
							{showHelp ? <RiArrowUpSLine size={20} /> : <RiArrowDownSLine size={20} />}
						</ListItemIcon>
					</ListItem>
					<Collapse in={showHelp}>
						<ListItem
							button
							component="a"
							target="_blank"
							rel="noopener noreferrer"
							className={classNames(classes.listItem, 'intercom_launcher')}
							href="mailto:cgregytq@rentle.intercom-mail.com"
							onClick={() => dispatch(closeSideBar())}
						>
							<ListItemIcon className={classes.listItemIcon} />
							<ListItemText
								primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: '400 !important' } }}
								primary={t('headerBar.contactSupport', 'Contact support')}
							/>
						</ListItem>
						<ListItem
							className={classes.listItem}
							button
							component="a"
							target="_blank"
							rel="noopener noreferrer"
							href="https://support.rentle.io"
							onClick={() => dispatch(closeSideBar())}
							sx={{
								marginBottom: theme.spacing(1),
							}}
						>
							<ListItemIcon className={classes.listItemIcon} />
							<ListItemText
								primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: '400 !important' } }}
								primary={'Help center'}
							/>
						</ListItem>
					</Collapse>
					<ListItem
						className={classes.listItem}
						button
						onClick={() => {
							sleekPlan.open();
						}}
						sx={{ mb: 1 }}
					>
						<ListItemIcon className={classes.listItemIcon}>
							<RiMessage2Line size={20} />
						</ListItemIcon>
						<ListItemText
							primaryTypographyProps={{
								variant: 'body2',
								sx: {
									fontWeight: '400 !important',
								},
							}}
							primary={t('headerBar.updates', 'Updates')}
						/>
					</ListItem>
					{showEarnCreditsBanner && (
						<Box p={1}>
							<EarnCreditsBanner
								onClick={() => {
									dispatch(ViewActions.toggleEarnCreditsModal(true));
									logEvent('earn_credits_banner_click');
								}}
								onClose={() => {
									hideEarnCreditsBanner(userId, shopInfo?.id);
									logEvent('earn_credits_banner_close', {
										shopId: shopInfo?.id,
										userId: userId ?? undefined,
									});
								}}
							/>
						</Box>
					)}
					<div className={classes.divider} />
					<ListItem button onClick={openBottomMenu}>
						<ListItemText
							primaryTypographyProps={{
								variant: 'body2',
								color: 'inherit',
								className: classes.bottomText,
							}}
							primary={user ? user.email : ''}
						/>
						<RiArrowUpSLine size={24} />
					</ListItem>
					<Menu
						id="simple-menu"
						anchorEl={bottomMenuAnchor}
						keepMounted
						open={Boolean(bottomMenuAnchor)}
						onClose={closeBottomMenu}
						onClick={(e) => e.stopPropagation()}
						classes={{
							paper: classes.bottomMenu,
						}}
					>
						<MenuItem
							onClick={() => {
								signOut();
							}}
							style={{ width: '100%' }}
						>
							{t('common:actions.logout', 'Log out')}
						</MenuItem>
					</Menu>
				</List>
			</div>
		</Drawer>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		nested: {
			paddingLeft: theme.spacing(3),
		},
		divider: {
			height: 1,
			width: '100%',
			background: theme.palette.divider,
		},
		capitalize: {
			textTransform: 'capitalize',
		},
		sidebarContainer: {
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'space-between',
			width: '210px',
		},
		sidebarMain: {
			flex: '1 1 auto',
			overflowY: 'scroll',
			msOverflowStyle: 'none',
			WebkitOverflowScrolling: 'touch',
			'&::-webkit-scrollbar': {
				display: 'none',
			},
		},
		sidebarBottom: {
			boxShadow: `0px -10px 30px 0px ${theme.palette.background.paper}`,
		},
		bottomText: {
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			display: 'block',
		},
		listItem: {
			padding: theme.spacing(0.5, 2),
		},
		listItemIcon: {
			color: theme.palette.text.secondary,
			position: 'relative',
		},

		navItem: {
			padding: theme.spacing(1, 2),
		},
		navItemActive: {
			background: theme.palette.action.selected,
		},
		updatesButton: {
			color: theme.palette.text.primary,
			padding: theme.spacing(0.5, 1),
			background: theme.palette.error.main,
			borderRadius: '5px',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
		},
		updatesButtonIcon: {
			color: theme.palette.common.white,
			marginRight: theme.spacing(0.5),
			fontSize: '18px',
		},
		updatesButtonText: {
			color: theme.palette.common.white,
		},
		upgradePlanBtn: {
			backgroundColor: theme.palette.colors.peach.light,
			borderRadius: '4px',
			padding: theme.spacing(1),
		},
		navItemIcon: {
			color: theme.palette.text.secondary,
			position: 'relative',
		},
		navItemIconPlus: {
			color: theme.palette.success.main,
			position: 'absolute',
			top: -10,
			left: 14,
			background: theme.palette.background.paper,
			borderRadius: '50%',
			width: 20,
			height: 20,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 10,
		},
		navItemTextLocked: {
			opacity: 0.3,
		},
		navItemIconActive: {
			color: theme.palette.primary.main,
		},
		bottomMenu: {
			minWidth: 180,
		},
	}),
);

export default SideBar;
